import BaseFormStore from "@components/common/form/stores/BaseFormStore";
import RootStore from "@stores/RootStore";
import { AxiosError } from "axios";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import UserAddressForm from "../forms/UserAddressForm";
import AddressType from "../models/AddressType";
import UserAddress from "../models/UserAddress";
import UserAddressOption from "../models/UserAddressOption";
import UserAddressService from "../services/UserAddressService";

export default class UserAddressEditViewStore extends BaseFormStore {
    rootStore: RootStore;
    service: UserAddressService;
    form: UserAddressForm;

    constructor(rootStore: RootStore) {
        super({
            fnGet: async (itemId) => await this.getUserAddressAsync(itemId)
        })

        this.rootStore = rootStore;
        this.service = new UserAddressService();

        this.form = new UserAddressForm(
            {
                onSuccess: () => {
                    this.onFormSuccess();
                },
                onError() { }
            },
            {},
            rootStore)

        makeObservable(this, {
            //#region UI
            isLoading: observable,
            setIsLoading: action,
            isUserAddressDialogOpen: observable,
            setIsUserAddressDialogOpen: action,
            //#endregion

            userAddresses: computed,
            getUserAddressAsync: action,
            userAddressOption: observable,
            setUserAddressOption: action,
        })
    }

    //#region UI

    isLoading: boolean = false;
    setIsLoading = (value: boolean) => {
        this.isLoading = value
    }

    //#endregion

    initialize = () => {
        if (this.userAddressOption === "new") {
            this.initializeForm(true, null);
        }
        else {
            this.initializeForm(false, this.userAddressOption.toString());
        }
    }

    isUserAddressDialogOpen: boolean = false;
    setIsUserAddressDialogOpen = (value: boolean) => {
        this.isUserAddressDialogOpen = value;
    }

    userAddressOption: UserAddressOption = "new";
    setUserAddressOption = (userAddressOption: UserAddressOption) => {
        runInAction(() => {
            this.userAddressOption = userAddressOption;
            this.initialize();
        })
    }

    get userAddresses() {
        return this.rootStore.authorizationStore.user?.userAddresses;
    }

    getUserAddressAsync = (addressType: string) => {
        return this.userAddresses?.find(a => a.type === this.getAddressTypeFromString(addressType));
    }

    getAddressTypeFromString = (addressType: string) => {
        switch (addressType) {
            case "home":
                return AddressType.home;
            case "work":
                return AddressType.work;
            case "other":
                return AddressType.other;
        }

        return undefined;
    }

    onFormSuccess = async () => {
        var userAddress = this.form.values() as UserAddress;
        await this.upsertAsync(userAddress);
    }

    upsertAsync = async (userAddress: UserAddress) => {
        var user = this.rootStore.authorizationStore.user;
        if (user === undefined || user === null) {
            return;
        }
        userAddress.userId = user.id;

        var addressType = this.getAddressTypeFromString(this.userAddressOption);
        if (addressType === undefined) {
            runInAction(() => {
                this.rootStore.orderEditViewStore.userAddress = userAddress;
                this.setIsUserAddressDialogOpen(false);
            })

            return;
        }
        userAddress.type = addressType

        var oldUserAddresses = _.cloneDeep(user.userAddresses) ?? [];
        var currentUserAddress = oldUserAddresses.find(a => a.type === addressType);
        if (currentUserAddress !== undefined) {
            if (currentUserAddress.address === userAddress.address && currentUserAddress.locationDescription === userAddress.locationDescription) {
                runInAction(() => {
                    this.rootStore.orderEditViewStore.userAddress = _.cloneDeep(userAddress);
                    this.setIsUserAddressDialogOpen(false);
                })

                return;
            }
        }

        try {
            this.setIsLoading(true);

            var updateResult = await this.service.upsertAsync(userAddress);
            if (updateResult.status === 200) {
                var newUserAddresses: UserAddress[] = [];
                for (var oldUserAddress of oldUserAddresses) {
                    if (oldUserAddress.type !== userAddress.type) {
                        newUserAddresses.push(oldUserAddress);
                    }
                }
                newUserAddresses.push(userAddress);
                user.userAddresses = newUserAddresses;

                runInAction(() => {
                    this.rootStore.orderEditViewStore.userAddress = _.cloneDeep(updateResult.data);
                    this.setIsUserAddressDialogOpen(false);
                })
            }

        } catch (ex) {
            if (ex instanceof AxiosError) {
                this.rootStore.toastStore.alert(
                    "error",
                    "Dogodila se neočekivana greška.",
                    "Molimo pokušaj ponovo.")
            }
        } finally {
            this.setIsLoading(false);
        }
    }
}